import React, { useEffect } from "react";
import { endOfMonth, parseISO, startOfMonth } from "date-fns";
import { useState } from "react";
import { BookingType, Space } from "../../../data";
import { FirstDayOfWeek, toDay } from "../../../lib/datetime";
import { AvailableDay } from "../../../offering/api";
import {
  useBestDayRatesQuery,
  getViewRange,
} from "../../hooks/use_best_day_rates_query";
import { useGetBetterHourRatesQuery } from "../../hooks/use_better_hour_rates_query";
import { toBestDateRates } from "../../utils/to_best_date_rates";
import { spaceAdvertisesVaryingPrices } from "../../utils/space_advertises_varying_prices";
import { CalendarSwitch } from "../calendar_switch";
import { TimeSlot } from "./checkout_datetime_picker";
import {
  getDurationInHours,
  Time,
  parseTime,
} from "../../../offering/time_utils";
import { useAnalytics } from "../../../lib/analytics/analytics";
import { Day } from "../../../offering/day_utils";
import {
  DateRangePicker,
  PickingRange,
} from "../date_range_picker/date_range_picker";
import { useShowDailyRateAccessMessageFeatureFlag } from "@/core/splitio";
import { DurationRangePicker } from "./duration_range_picker";
import { colors } from "../colors";
import { useI18n } from "@/lib/i18n/i18n_provider";
import { getBookableHoursForDailyMessage } from "../../utils/get_bookable_hours_for_daily_message";
import { DailyBookableHoursMessage } from "../daily_bookable_hours_message";

export interface HourlyValue {
  date?: Day;
  startTime?: Time;
  endTime?: Time;
}

export interface DailyValue {
  startDate?: Day;
  endDate?: Day;
}

export type DateTimeValue = HourlyValue | DailyValue;

export const isHourlyValue = (value?: any): value is HourlyValue =>
  !!value && "date" in value;

// Narrow a DateTimeValue to its daily shape, or undefined if it isn't daily.
// Useful at consumer sites that only render the daily branch and feed the
// dateRange to APIs / components that still take DayPassDateRange (e.g.
// CheckoutDatePicker, useSpaceAvailableDaysQuery). Returns undefined for
// hourly values so the consumer falls through to its "no date" state.
export const asDailyDateRange = (
  value?: DateTimeValue
): DailyValue | undefined =>
  value && !isHourlyValue(value) ? value : undefined;

interface DateTimePickerProps<T extends DateTimeValue> {
  switchValue: BookingType;
  space?: Space | null;
  timeslots: TimeSlot[];
  availableDays?: AvailableDay[];
  onChangeMonth: (startDate: Date, endDate: Date) => void;
  onSwitch: (value: BookingType) => void;
  hideSwitch?: boolean;
  value?: T;
  onChange: (value: T) => void;
  pickingRange: PickingRange;
  spaceLayoutId?: string;
  disableSelectPastMonth?: boolean;
  firstDayOfWeek?: FirstDayOfWeek;
  // Whether the slot request for the selected date is still in flight. Callers
  // that build their slots synchronously leave this unset; an async caller must
  // pass it, because an empty slot list on its own cannot say whether the day
  // is genuinely unbookable or the answer simply has not landed yet.
  timeslotsLoading?: boolean;
  // Dates to tint as belonging to the recurring booking being drafted.
  markedDates?: Date[];
}

export function DateTimePicker<T extends DateTimeValue>({
  switchValue,
  space,
  timeslots,
  availableDays,
  onChangeMonth,
  onSwitch,
  hideSwitch,
  value,
  onChange,
  pickingRange,
  spaceLayoutId,
  disableSelectPastMonth,
  firstDayOfWeek,
  timeslotsLoading,
  markedDates,
}: DateTimePickerProps<T>) {
  const { fbt } = useI18n();

  const showDailyRateAccessMessage = useShowDailyRateAccessMessageFeatureFlag(
    space?.partnerID || ""
  );

  const dateValue =
    isHourlyValue(value) && !!value.date
      ? {
          startDate: parseISO(value.date),
          endDate: parseISO(value.date),
        }
      : !isHourlyValue(value)
      ? {
          startDate: value?.startDate ? parseISO(value.startDate) : undefined,
          endDate: value?.endDate ? parseISO(value.endDate) : undefined,
        }
      : {};

  const analytics = useAnalytics();

  const isHourlySwitch = switchValue === BookingType.Hourly;

  const [viewingRange, setViewingRange] = useState<{
    startDate: Date;
    endDate: Date;
  }>();

  const { data: bestDayRates, isLoading: bestDayRatesLoading } =
    useBestDayRatesQuery(
      getViewRange(viewingRange, {
        startDate: isHourlyValue(value) ? value?.date : value?.startDate,
        endDate: isHourlyValue(value) ? value?.date : value?.endDate,
      }),
      isHourlySwitch ? BookingType.Hourly : BookingType.Daily,
      space?.id,
      spaceLayoutId
    );

  // The rack rate for the active booking type, as the backend resolved it. It
  // already accounts for `use_layout_prices`, day-of-week overrides and pricing
  // time bands, none of which the space payload exposes — which is why the old
  // client-side derivation advertised prices checkout would not honour.
  const originalUnitPrice = bestDayRates.originalUnitPrice;
  // Wait for the day rates' first settle before seeding the better-hour-rates
  // query: this value is part of its request key, so seeding with the rack rate
  // fires one request now and a second when options arrive — and the
  // slot-rate dots re-derive from whichever answered last. The hook serves the
  // last settled payload across key changes, so this only blanks before the
  // very first response. `||` is deliberate: the downstream request guards are
  // truthiness-based, so a $0 day rate must fall back to the rack rate or
  // those requests never fire at all.
  const hourlyUnitPrice =
    isHourlyValue(value) && value.date && !bestDayRatesLoading
      ? bestDayRates.options[value.date] || originalUnitPrice
      : undefined;

  const betterHourRates = useGetBetterHourRatesQuery(
    timeslots,
    hourlyUnitPrice,
    isHourlyValue(value) ? value : undefined,
    space?.id,
    space?.currencyCode,
    spaceLayoutId
  );

  const handleChangeDate = (value: { startDate?: Date; endDate?: Date }) => {
    if (isHourlySwitch) {
      onChange({ date: value.startDate && toDay(value.startDate) } as T);
    } else {
      onChange({
        startDate: value.startDate && toDay(value.startDate),
        endDate: value.endDate && toDay(value.endDate),
      } as T);
    }
  };

  useEffect(() => {
    if (isHourlyValue(value) && betterHourRates.recommendation) {
      const { date, startTime, endTime } = value || {};
      if (date && startTime && endTime) {
        const duration = getDurationInHours(startTime, endTime);
        analytics.event(
          "Date Select DP Display",
          {
            "Purchased Products": {
              item_id: space?.id,
              item_name: space?.name,
              quantity: duration,
              price: originalUnitPrice,
              discount: betterHourRates.dynamicPrice?.[date],
            },
            Date: date,
            "Start time": startTime,
            "End Time": endTime,
            Duration: duration,
            "Discount Display": betterHourRates.recommendation,
            Source: "WL",
          },
          { onlyProviders: ["mixpanel", "customerio"] }
        );
      }
    }
  }, [
    betterHourRates.recommendation,
    value,
    space?.id,
    space?.name,
    analytics,
    originalUnitPrice,
    betterHourRates.dynamicPrice,
  ]);

  const dynamicPricings = toBestDateRates(
    bestDayRates.options,
    betterHourRates.dynamicPrice,
    space?.currencyCode
  );

  const availableDates = isHourlySwitch
    ? availableDays
        ?.filter((a) => a.availableHourly !== false)
        .map((a) => parseISO(a.date))
    : availableDays
        ?.filter((aD) => aD.availableFullDay)
        .map((aD) => parseISO(aD.date));

  const handleViewDates = (
    startDate: Date,
    endDate: Date,
    viewingDate: Date
  ) => {
    onChangeMonth(startDate, endDate);
    setViewingRange({
      startDate: startOfMonth(viewingDate),
      endDate: endOfMonth(viewingDate),
    });
  };

  const bookingHoursForDailyMessage =
    space?.dailyAccessBookingHours ?? space?.bookingHours ?? [];
  const dailyBookableHours =
    !isHourlySwitch && bookingHoursForDailyMessage.length
      ? getBookableHoursForDailyMessage(
          bookingHoursForDailyMessage,
          !isHourlyValue(value) ? value?.startDate : undefined,
          !isHourlyValue(value) ? value?.endDate : undefined
        )
      : null;

  const dailyBookableStartTime = dailyBookableHours
    ? fbt.formatTime(parseTime(dailyBookableHours.openAt))
    : undefined;
  const dailyBookableEndTime = dailyBookableHours
    ? fbt.formatTime(parseTime(dailyBookableHours.closedAt))
    : undefined;

  // Once a date is picked, the slot list alone cannot explain itself: zero slots
  // is both what a pending request and a fully-unbookable day look like. The
  // loading flag is the only thing that separates them, so a day the backend
  // answers with no slots gets a terminal message instead of a spinner that
  // outlives its own request.
  const getSlotHint = () => {
    if (!dateValue.startDate) {
      return undefined;
    }
    if (timeslotsLoading) {
      return fbt("Checking available start times...");
    }
    if (!betterHourRates.timeSlots.length) {
      return fbt("No available start times for this date.");
    }
    return undefined;
  };
  const slotHint = getSlotHint();

  const dailyAccessHoursMessage =
    showDailyRateAccessMessage &&
    switchValue === BookingType.Daily &&
    dailyBookableStartTime &&
    dailyBookableEndTime ? (
      <DailyBookableHoursMessage
        bookableStartTime={dailyBookableStartTime}
        bookableEndTime={dailyBookableEndTime}
      />
    ) : null;

  return (
    <div>
      {(!hideSwitch || dailyAccessHoursMessage) && (
        <div className="px-4 py-3 border-bottom">
          {!hideSwitch && (
            <CalendarSwitch value={switchValue} onChange={onSwitch} />
          )}
          {dailyAccessHoursMessage}
        </div>
      )}
      <div>
        <DateRangePicker
          availableDates={availableDates}
          currency={space?.currencyCode || undefined}
          dynamicPricings={dynamicPricings}
          originalUnitPrice={originalUnitPrice}
          hidePrice={!spaceAdvertisesVaryingPrices(space)}
          onChange={handleChangeDate}
          onViewDates={handleViewDates}
          value={dateValue}
          pickingRange={pickingRange}
          partnerId={space?.partnerID}
          displayPlus={
            isHourlySwitch &&
            !!(space?.priceRanges.hourly?.lowestPrice && originalUnitPrice) &&
            space.priceRanges.hourly.lowestPrice < originalUnitPrice
          }
          disableSelectPastMonth={disableSelectPastMonth}
          firstDayOfWeek={firstDayOfWeek}
          markedDates={markedDates}
        />
        {isHourlySwitch && (
          <div>
            {!dateValue.startDate && space ? (
              <div
                className="small text-center m-3 small-price-text"
                style={{ color: colors.blackMinus30 }}
              >
                {fbt("Prices vary by day. Select a date to choose a time.")}
              </div>
            ) : slotHint ? (
              <div
                className="small text-center m-3 small-availability-text"
                style={{ color: colors.blackMinus30 }}
              >
                {slotHint}
              </div>
            ) : null}
            {betterHourRates.recommendation && (
              <div className="text-success small text-center m-3">
                {betterHourRates.recommendation}
              </div>
            )}
            <div
              data-testid="time-range-picker-container"
              className="time-range-picker d-flex justify-content-between m-3"
            >
              <DurationRangePicker
                onChangeTime={(timeValue) => {
                  onChange({
                    startTime: timeValue.start,
                    endTime: timeValue.end,
                  } as T);
                }}
                timeslots={betterHourRates.timeSlots}
                timeslotsLoading={timeslotsLoading}
                value={{
                  start: isHourlyValue(value) ? value?.startTime : undefined,
                  end: isHourlyValue(value) ? value?.endTime : undefined,
                }}
                spaceId={space?.id}
                date={dateValue.startDate && toDay(dateValue.startDate)}
                unitPrice={hourlyUnitPrice}
                currency={space?.currencyCode || undefined}
                spaceLayoutId={spaceLayoutId}
              />
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
